import React, { useMemo, useState } from 'react';
import { useDidMount, useTranslation } from '@bcp-channels-sharedcomponents-reactjs/core/dist/hooks';
import { useBuildPhoneCodes, useReactiveForm } from '@hooks';
import { useNavigation } from '@navigation';
import { FormHookOutput, useForm } from '@bcp-channels-sharedcomponents-reactjs/form-utils/dist/$components';
import { FormHelper } from '@bcp-channels-sharedcomponents-reactjs/form-utils/dist/helpers';
import { CoreHelper, ValidationHelper } from '@bcp-channels-sharedcomponents-reactjs/core/dist/$helpers';
import { useFetchClickToCallAgentClosed } from '@services';
import { useServiceResponse } from '@store';
import { InitDateState, PopupMenuItem, PopupMenuRef } from '@bcp-nextgen-dx-component-factory/accolade-design-system';
import dayjs from 'dayjs';
import { ClickToCallAgentClosedInputDto } from '@api/izibizi-xp';
import { ActiveItem } from '@bcp-nextgen-dx-component-factory/accolade-design-system/dist/molecules/InputSelect/InputSelect.models';

interface RequestToCallCloseResources {
	title: string;
	subtitle: string;
	dateLabel: string;
	timeLabel: string;
	titleForm: string;
	inputLabelContact: string;
	inputLabelEmail: string;
	inputLabelName: string;
	inputCountry: string;
	linkBack: string;
	sendRequest: string;
	required: string;
	invalidEmail: string;
	invalidPhone: string;
	invalidDate: string;
}

type DateStatus = 'default' | 'error';

interface RequestToCallCloseOutput {
	resources: RequestToCallCloseResources;
	buildDialingPhoneCodes: PopupMenuItem[];
	buildTimeCodes: PopupMenuItem[];
	timeSchedule: string;
	indicative: string;
	name: string;
	date: dayjs.Dayjs | undefined;
	form: FormHookOutput;
	isValid?: boolean;
	clickToCallAgentClosedSuccess: boolean;
	statusDate: DateStatus;
	messageErrorDate: string | undefined;
	isDisabled: boolean;
	popupMenuRefIndicative: React.RefObject<PopupMenuRef>;
	popupMenuRefTime: React.RefObject<PopupMenuRef>;
	getInputProps: (name: string) => void;
	handlePressIndicative: (id: ActiveItem | undefined) => void;
	handlePressTime: (id: ActiveItem | undefined) => void;
	back: () => void;
	handleOnSubmit: () => void;
	onDateChange: (date: InitDateState) => void;
	onClearDate: () => void;
}

export const useRequestToCallCloseHelper = (): RequestToCallCloseOutput => {
	const t = useTranslation();
	const { back } = useNavigation();
	const { fetch: fetchClickToCallAgentClosed, success: clickToCallAgentClosedSuccess } =
		useFetchClickToCallAgentClosed();
	const { clickToCallIsOpen } = useServiceResponse();
	const { phoneCodes, initialPhoneId, getSelectedPhone } = useBuildPhoneCodes({});
	const [date, setDate] = useState<dayjs.Dayjs | undefined>(dayjs());
	const [statusDate, setStatusDate] = useState<DateStatus>('default');
	const [messageErrorDate, setMessageErrorDate] = useState<string | undefined>(undefined);
	const popupMenuRefIndicative = React.useRef<PopupMenuRef>(null);
	const popupMenuRefTime = React.useRef<PopupMenuRef>(null);

	const [timeSchedule, setTimeSchedule] = useState<string>('08:00 - 10:00');

	useDidMount(() => {
		popupMenuRefIndicative.current?.setActiveItem(initialPhoneId);
		if (initialPhoneId !== undefined) {
			const selectedPhone = getSelectedPhone(phoneCodes[initialPhoneId].content as string);
			setValues({
				...values,
				indicative: selectedPhone.phonePrefix,
				country: selectedPhone.code as string,
			});
		}
		popupMenuRefTime.current?.setActiveItem(0);
	});

	const buildTimeCodes = useMemo(() => {
		const timeSchedule = !CoreHelper.isNullOrEmpty(clickToCallIsOpen?.schedule)
			? (clickToCallIsOpen?.schedule as string[])
			: [];
		const result: PopupMenuItem[] = timeSchedule.map((time) => {
			return {
				content: `${time}`,
			};
		});
		return result;
	}, [clickToCallIsOpen?.schedule]);

	const resources = useMemo((): RequestToCallCloseResources => {
		return {
			title: t('placeholders.requestToCall.close.title'),
			subtitle: t('placeholders.requestToCall.close.subtitle'),
			dateLabel: t('placeholders.requestToCall.form.date'),
			timeLabel: t('placeholders.requestToCall.form.time'),
			titleForm: t('placeholders.requestToCall.form.title'),
			inputLabelContact: t('placeholders.requestToCall.form.phone'),
			inputLabelEmail: t('general.email'),
			inputLabelName: t('placeholders.requestToCall.form.name'),
			inputCountry: t('placeholders.requestToCall.form.country'),
			linkBack: t('general.back'),
			sendRequest: t('placeholders.requestToCall.form.sendRequest'),
			required: t('general.errors.required'),
			invalidEmail: t('general.errors.invalidEmail'),
			invalidPhone: t('general.errors.invalidPhone'),
			invalidDate: t('general.errors.invalidDate'),
		};
	}, [t]);

	const handlePressIndicative = (item: ActiveItem | undefined) => {
		if (item?.id !== undefined) {
			const selectedPhone = getSelectedPhone(item.content);
			if (selectedPhone !== undefined && selectedPhone.phonePrefix !== undefined) {
				handleOnChangeField(['indicative', 'phoneNumber', 'country'])([
					selectedPhone.phonePrefix as string,
					'',
					selectedPhone.code as string,
				]);
			}
		}
		popupMenuRefIndicative.current?.hide();
	};

	const handlePressTime = (item: ActiveItem | undefined) => {
		if (item?.id !== undefined) {
			const time = item.content;
			setTimeSchedule(time);
		}
		popupMenuRefTime.current?.hide();
	};

	const handleOnSubmit = () => {
		const phone = `${values.indicative}${values.phoneNumber}`;
		const data: ClickToCallAgentClosedInputDto = {
			email: values.email,
			phoneNumber: phone,
			callDate: date?.format('DD/MM/YYYY'),
			name: values.name,
			phoneCountryCode: values.country,
			callHour: timeSchedule,
		};
		fetchClickToCallAgentClosed(data);
	};

	const form = useForm('requestToCall');
	const { getInputProps, isValid, values, handleOnChangeField, setValues } = useReactiveForm({
		name: form.name,
		onSubmit: handleOnSubmit,
		validate: (values) => {
			const isPT = values.indicative !== undefined && values.indicative === '+351';
			return {
				name: FormHelper.validateField(values.name, [ValidationHelper.validateRequired(resources.required)]),
				email: FormHelper.validateField(values.email, [
					ValidationHelper.validateRequired(resources.required),
					ValidationHelper.validateEmail(resources.invalidEmail),
				]),
				phoneNumber: FormHelper.validateField(values.phoneNumber, [
					ValidationHelper.validateRequired(resources.required),
					isPT ? ValidationHelper.validateEqualLength(9, resources.invalidPhone) : () => undefined,
				]),
				country: FormHelper.validateField(values.country, []),
			};
		},
	});

	const onDateChange = (newDate: InitDateState) => {
		if (newDate.selectedDate === undefined) {
			return;
		}
		if (newDate.selectedDate?.isBefore(date, 'day') && !newDate.selectedDate.isSame(date, 'day')) {
			setStatusDate('error');
			setMessageErrorDate(resources.invalidDate);
		} else {
			setDate(newDate.selectedDate);
			setStatusDate('default');
			setMessageErrorDate(undefined);
		}
	};

	const onClearDate = () => {
		setDate(undefined);
		setStatusDate('error');
		setMessageErrorDate(resources.required);
	};

	const isDisabled = useMemo(() => {
		return !isValid || (isValid && statusDate === 'error' && !CoreHelper.isNullOrEmpty(messageErrorDate));
	}, [isValid, statusDate, messageErrorDate]);

	return {
		resources,
		buildDialingPhoneCodes: phoneCodes,
		buildTimeCodes,
		timeSchedule,
		indicative: values.indicative,
		date,
		name: values.name,
		form,
		isValid,
		clickToCallAgentClosedSuccess,
		statusDate,
		messageErrorDate,
		isDisabled,
		popupMenuRefIndicative,
		popupMenuRefTime,
		getInputProps,
		back,
		handleOnSubmit,
		handlePressTime,
		handlePressIndicative,
		onDateChange,
		onClearDate,
	};
};
